import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { LineChart, Loader2, Clock, Thermometer, Cloud, Filter, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { DateRangePicker } from './ui/date-range-picker';

interface GraphModalProps {
    isOpen: boolean;
    onClose: () => void;
    edgeUid?: string;
    refreshIntervalSeconds?: number;
    coatingType?: string;
}

type GraphTimeRange = 'daterange' | '15min' | '30min' | '1hour' | '4hours';

interface GraphDateRange {
    dateFrom: Date | undefined;
    dateTo: Date | undefined;
}

export const GraphModal: React.FC<GraphModalProps> = ({
    isOpen,
    onClose,
    edgeUid,
    refreshIntervalSeconds = 3,
    coatingType,
}) => {
    const [graphData, setGraphData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState<GraphTimeRange>('1hour');
    const [dateRange, setDateRange] = useState<GraphDateRange>({
        dateFrom: undefined,
        dateTo: undefined,
    });
    
    const normalizedCoatingType = (coatingType || '').toString().toLowerCase();
    const isRaptor = normalizedCoatingType === 'raptor';
    
    const [activeTab, setActiveTab] = useState<'temperature' | 'pressure' | 'fbe' | 'cmpp'>(isRaptor ? 'fbe' : 'temperature');

    const tabsContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
    const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

    const updateTabScrollButtons = useCallback(() => {
        const el = tabsContainerRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanScrollTabsLeft(scrollLeft > 1);
        setCanScrollTabsRight(scrollLeft < scrollWidth - clientWidth - 1);
    }, []);

    useEffect(() => {
        const el = tabsContainerRef.current;
        if (!el) return;
        updateTabScrollButtons();
        const onScroll = () => updateTabScrollButtons();
        el.addEventListener('scroll', onScroll, { passive: true });
        const ro = new ResizeObserver(() => updateTabScrollButtons());
        ro.observe(el);
        const tabList = el.firstElementChild;
        if (tabList) ro.observe(tabList);
        window.addEventListener('resize', updateTabScrollButtons);
        return () => {
            el.removeEventListener('scroll', onScroll);
            ro.disconnect();
            window.removeEventListener('resize', updateTabScrollButtons);
        };
    }, [isRaptor, updateTabScrollButtons]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => updateTabScrollButtons());
        return () => cancelAnimationFrame(frame);
    }, [activeTab, updateTabScrollButtons]);

    useEffect(() => {
        const container = tabsContainerRef.current;
        if (!container) return;

        const activeTrigger = container.querySelector<HTMLElement>('[data-state="active"]');
        if (activeTrigger) {
            const { offsetLeft, offsetWidth } = activeTrigger;
            const { scrollLeft, clientWidth } = container;
            const isFullyVisible =
                offsetLeft >= scrollLeft &&
                offsetLeft + offsetWidth <= scrollLeft + clientWidth;

            if (!isFullyVisible) {
                activeTrigger.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest',
                });
            }
            updateTabScrollButtons();
        }
    }, [activeTab, updateTabScrollButtons]);

    const handleScrollTabsLeft = () => {
        tabsContainerRef.current?.scrollBy({ left: -160, behavior: 'smooth' });
    };

    const handleScrollTabsRight = () => {
        tabsContainerRef.current?.scrollBy({ left: 160, behavior: 'smooth' });
    };
    
    // For temperature/FBE: SATs (1-30) or FBE fields (fbeFlow_1-10, fbeCarrier_1-10)
    const [selectedTemperatureSats, setSelectedTemperatureSats] = useState<Set<number>>(() => {
        if (isRaptor) {
            // For FBE, we'll use numbers 1-20 (10 flows + 10 carriers)
            const allFields = new Set<number>();
            for (let i = 1; i <= 20; i++) {
                allFields.add(i);
            }
            return allFields;
        } else {
            // Initialize with all SATs selected by default
            const allSats = new Set<number>();
            for (let i = 1; i <= 30; i++) {
                allSats.add(i);
            }
            return allSats;
        }
    });
    const [showTemperatureFilter, setShowTemperatureFilter] = useState(true);
    
    // For pressure/CMPP: pressure fields or CMPP fields (cmppFlow_1-10, cmppCarrier_1-10)
    const [selectedPressureFields, setSelectedPressureFields] = useState<Set<string>>(() => {
        if (isRaptor) {
            // For CMPP, initialize with all CMPP fields
            const allFields = new Set<string>();
            for (let i = 1; i <= 10; i++) {
                allFields.add(`cmppFlow_${i}`);
                allFields.add(`cmppCarrier_${i}`);
            }
            return allFields;
        } else {
            // Initialize with all pressure fields selected by default
            return new Set(['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure']);
        }
    });
    const [showPressureFilter, setShowPressureFilter] = useState(true);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const currentRefreshIntervalRef = useRef<number>(refreshIntervalSeconds);

    // Update ref when refreshIntervalSeconds changes
    useEffect(() => {
        currentRefreshIntervalRef.current = refreshIntervalSeconds;
    }, [refreshIntervalSeconds]);

    // Helper function to format date in UTC as DD-MM-YYYY HH:mm:ss.SSS (for API requests)
    const formatUTCTimestamp = (date: Date): string => {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const seconds = date.getUTCSeconds().toString().padStart(2, '0');
        const milliseconds = date.getUTCMilliseconds().toString().padStart(3, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    };

    // Helper function to format date in local time as DD-MM-YYYY HH:mm:ss.SSS (for display in graphs)
    const formatLocalTimestamp = (date: Date): string => {
        // console
        return moment.utc(date, "DD-MM-YYYY HH:mm:ss.SSS").local().format("DD-MM-YYYY HH:mm:ss.SSS");
    };

    // Calculate timestamps based on time range (in milliseconds UTC)
    const getTimestamps = () => {
        const now = new Date();
        let endTimestamp = now.getTime();

        let startTimestamp: number;
        switch (timeRange) {
            case 'daterange': {
                if (!dateRange.dateFrom) {
                    startTimestamp = now.getTime() - 60 * 60 * 1000;
                    break;
                }
                startTimestamp = dateRange.dateFrom.getTime();
                endTimestamp = dateRange.dateTo
                    ? dateRange.dateTo.getTime()
                    : now.getTime();
                break;
            }
            case '15min':
                startTimestamp = now.getTime() - 15 * 60 * 1000;
                break;
            case '30min':
                startTimestamp = now.getTime() - 30 * 60 * 1000;
                break;
            case '1hour':
                startTimestamp = now.getTime() - 60 * 60 * 1000;
                break;
            case '4hours':
                startTimestamp = now.getTime() - 4 * 60 * 60 * 1000;
                break;
            default:
                startTimestamp = now.getTime() - 60 * 60 * 1000;
        }
        
        return { startTimestamp, endTimestamp };
    };

    // Fetch graph data from API
    const fetchGraphData = async () => {
        if (!edgeUid || !isOpen) {
            return;
        }

        if (timeRange === 'daterange' && !dateRange.dateFrom) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const { startTimestamp, endTimestamp } = getTimestamps();
            const apiUrl =
                 process.env.REACT_APP_LIMITS_API_BASE_URL ||
                 process.env.REACT_NEW_REPORT_GENERATION_API_BASE_URL ||
                'http://localhost:8000/v1';
            // Format timestamps in UTC format: DD-MM-YYYY HH:mm:ss.SSS
            const startTimestampStr = formatUTCTimestamp(new Date(startTimestamp));
            const endTimestampStr = formatUTCTimestamp(new Date(endTimestamp));
            const endpoint = `${apiUrl}/coating-data/graph-data`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    edge_uid: edgeUid,
                    start_timestamp: startTimestampStr,
                    end_timestamp: endTimestampStr,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
           // console.log('Graph API Response:', responseData);
            
            // Try different possible response structures
            const data = responseData?.data?.data || responseData?.data || responseData;
          //  console.log('Processed Graph Data:', data);
        //    console.log('Has setpoint:', !!data?.setpoint);
        //    console.log('Has actuals:', !!data?.actuals, 'Type:', Array.isArray(data?.actuals));
            
            setGraphData(data);

            // Update refresh interval if provided in API response
            if (data?.refresh_interval_seconds !== undefined) {
                currentRefreshIntervalRef.current = data.refresh_interval_seconds;
            }
        } catch (err: any) {
            console.error('Error fetching graph data:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                edgeUid,
                endpoint: `${process.env.REACT_APP_LIMITS_API_BASE_URL || process.env.REACT_NEW_REPORT_GENERATION_API_BASE_URL || 'http://localhost:9000'}/coating-data/graph-data`
            });
            setError(err.message || 'Failed to fetch graph data');
        } finally {
            setIsLoading(false);
        }
    };

    // Set up polling when modal opens or time range changes
    useEffect(() => {
        if (isOpen && edgeUid) {
            // Fetch immediately
            fetchGraphData();

            // Set up polling interval
            const intervalMs = currentRefreshIntervalRef.current * 1000;
            pollingIntervalRef.current = setInterval(() => {
                if (isOpen) {
                    fetchGraphData();
                }
            }, intervalMs);

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            };
        } else {
            // Clear interval when modal closes
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, edgeUid, timeRange, dateRange.dateFrom, dateRange.dateTo]);

    // Prepare temperature/FBE chart data (memoized — only rebuild when API data / coating type changes)
    const temperatureChartData = useMemo(() => {
        if (!graphData) {
            console.log('No graphData available');
            return null;
        }
        
        if (!graphData.setpoint) {
            console.log('No setpoint in graphData:', graphData);
            return null;
        }
        
        if (!graphData.actuals || !Array.isArray(graphData.actuals)) {
            console.log('No actuals array in graphData:', graphData);
            return null;
        }
        
        if (graphData.actuals.length === 0) {
            console.log('Actuals array is empty');
            return null;
        }

        const timeStamps: string[] = [];
        const temperatureSetpointData: { [key: string]: number[] } = {};
        const temperatureActualData: { [key: string]: number[] } = {};

        if (isRaptor) {
            // Process FBE data: fbeFlow_1-10 and fbeCarrier_1-10
            const fbeFields: string[] = [];
            for (let i = 1; i <= 10; i++) {
                fbeFields.push(`fbeFlow_${i}`);
                fbeFields.push(`fbeCarrier_${i}`);
            }

            // Process setpoint data for FBE
            const recipe = graphData.setpoint?.recipe || {};
            const bed1 = recipe?.fbeBedOneSP || {};
            const bed2 = recipe?.fbeBedTwoSP || {};

            fbeFields.forEach((field) => {
                const match = field.match(/(\w+)_(\d+)/);
                if (match) {
                    const [, type, numStr] = match;
                    const num = parseInt(numStr);
                    const bed = num <= 5 ? bed1 : bed2;
                    const idx = num <= 5 ? num : num - 5;
                    
                    let setpointValue: number | undefined;
                    if (type === 'fbeFlow') {
                        setpointValue = bed?.[`flowNozzleSp_${idx}`];
                    } else if (type === 'fbeCarrier') {
                        setpointValue = bed?.[`carrierNozzleSp_${idx}`];
                    }
                    
                    if (setpointValue !== undefined) {
                        temperatureSetpointData[field] = Array(graphData.actuals.length).fill(setpointValue);
                    }
                }
            });

            // Process actuals data for FBE
            graphData.actuals.forEach((actual: any) => {
                fbeFields.forEach((field) => {
                    const actualValue = actual[field];
                    if (actualValue !== undefined) {
                        if (!temperatureActualData[field]) {
                            temperatureActualData[field] = [];
                        }
                        temperatureActualData[field].push(actualValue);
                    }
                });
            });
        } else {
            // Process setpoint data (sat_1 to sat_30)
            for (let i = 1; i <= 30; i++) {
                const satKey = `sat_${i}`;
                const setpointValue = graphData.setpoint[satKey];
                if (setpointValue !== undefined) {
                    // Setpoint is constant for all time points
                    temperatureSetpointData[satKey] = Array(graphData.actuals.length).fill(setpointValue);
                }
            }

            // Process actuals data (sat_1 to sat_30)
            graphData.actuals.forEach((actual: any) => {
                for (let i = 1; i <= 30; i++) {
                    const satKey = `sat_${i}`;
                    const actualValue = actual[satKey];
                    if (actualValue !== undefined) {
                        if (!temperatureActualData[satKey]) {
                            temperatureActualData[satKey] = [];
                        }
                        temperatureActualData[satKey].push(actualValue);
                    }
                }
            });
        }

        // Generate time stamps from actuals timestamps (convert UTC to local time for display)
        graphData.actuals.forEach((actual: any, index: number) => {
            if (actual.timestamp) {
                try {
                    timeStamps.push(formatLocalTimestamp(actual.timestamp));
                } catch {
                    timeStamps.push(`T${index + 1}`);
                }
            } else {
                timeStamps.push(`T${index + 1}`);
            }
        });

        return {
            timeStamps,
            temperatureSetpointData,
            temperatureActualData,
        };
    }, [graphData, isRaptor]);

    // Prepare pressure/CMPP chart data (memoized — only rebuild when API data / coating type changes)
    const pressureChartData = useMemo(() => {
        if (!graphData) {
            return null;
        }
        
        if (!graphData.setpoint) {
            return null;
        }
        
        if (!graphData.actuals || !Array.isArray(graphData.actuals)) {
            return null;
        }
        
        if (graphData.actuals.length === 0) {
            return null;
        }

        const timeStamps: string[] = [];
        const pressureSetpointData: { [key: string]: number[] } = {};
        const pressureActualData: { [key: string]: number[] } = {};

        if (isRaptor) {
            // Process CMPP data: cmppFlow_1-10 and cmppCarrier_1-10
            const cmppFields: string[] = [];
            for (let i = 1; i <= 10; i++) {
                cmppFields.push(`cmppFlow_${i}`);
                cmppFields.push(`cmppCarrier_${i}`);
            }

            // Process setpoint data for CMPP
            const recipe = graphData.setpoint?.recipe || {};
            const bed1 = recipe?.cmppBedOneSP || {};
            const bed2 = recipe?.cmppBedTwoSP || {};

            cmppFields.forEach((field) => {
                const match = field.match(/(\w+)_(\d+)/);
                if (match) {
                    const [, type, numStr] = match;
                    const num = parseInt(numStr);
                    const bed = num <= 5 ? bed1 : bed2;
                    const idx = num <= 5 ? num : num - 5;
                    
                    let setpointValue: number | undefined;
                    if (type === 'cmppFlow') {
                        setpointValue = bed?.[`flowNozzleSp_${idx}`];
                    } else if (type === 'cmppCarrier') {
                        setpointValue = bed?.[`carrierNozzleSp_${idx}`];
                    }
                    
                    if (setpointValue !== undefined) {
                        pressureSetpointData[field] = Array(graphData.actuals.length).fill(setpointValue);
                    }
                }
            });

            // Process actuals data for CMPP
            graphData.actuals.forEach((actual: any) => {
                cmppFields.forEach((field) => {
                    // Handle case-insensitive field names
                    const actualValue = actual[field] ?? actual[field.toLowerCase()] ?? actual[`cmppflow_${field.match(/_(\d+)$/)?.[1]}`];
                    if (actualValue !== undefined) {
                        if (!pressureActualData[field]) {
                            pressureActualData[field] = [];
                        }
                        pressureActualData[field].push(actualValue);
                    }
                });
            });
        } else {
            // Process setpoint data for pressure fields
            const pressureFields = ['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure'];
            pressureFields.forEach(field => {
                const setpointValue = graphData.setpoint[field];
                if (setpointValue !== undefined) {
                    // Setpoint is constant for all time points
                    pressureSetpointData[field] = Array(graphData.actuals.length).fill(setpointValue);
                }
            });

            // Process actuals data for pressure fields
            graphData.actuals.forEach((actual: any) => {
                pressureFields.forEach(field => {
                    const actualValue = actual[field];
                    if (actualValue !== undefined) {
                        if (!pressureActualData[field]) {
                            pressureActualData[field] = [];
                        }
                        pressureActualData[field].push(actualValue);
                    }
                });
            });
        }

        // Generate time stamps from actuals timestamps (convert UTC to local time for display)
        graphData.actuals.forEach((actual: any, index: number) => {
            if (actual.timestamp) {
                try {
                    timeStamps.push(formatLocalTimestamp(actual.timestamp));
                } catch {
                    timeStamps.push(`T${index + 1}`);
                }
            } else {
                timeStamps.push(`T${index + 1}`);
            }
        });

        return {
            timeStamps,
            pressureSetpointData,
            pressureActualData,
        };
    }, [graphData, isRaptor]);

    const buildChartAxisLayout = (yAxisName: string, yAxisLabelFormatter: string) => ({
        tooltip: {
            trigger: 'axis' as const,
            confine: true,
            appendToBody: true,
            enterable: true,
            extraCssText: 'max-height: 320px; overflow-y: auto; pointer-events: auto;',
            axisPointer: {
                type: 'line' as const,
                snap: true,
            },
            position: (
                point: number[],
                _params: unknown,
                _dom: HTMLElement,
                _rect: unknown,
                size: { contentSize: number[]; viewSize: number[] }
            ) => {
                const [x, y] = point;
                const tooltipWidth = size.contentSize[0];
                const tooltipHeight = size.contentSize[1];
                const viewWidth = size.viewSize[0];
                const viewHeight = size.viewSize[1];

                let left = x + 20;
                if (left + tooltipWidth > viewWidth - 12) {
                    left = Math.max(12, x - tooltipWidth - 20);
                }

                let top = y - tooltipHeight / 2;
                top = Math.max(12, Math.min(top, viewHeight - tooltipHeight - 12));

                return [left, top];
            },
        },
        grid: {
            left: 20,
            right: 28,
            bottom: '22%',
            top: 56,
            containLabel: true,
        },
        yAxis: {
            type: 'value' as const,
            name: yAxisName,
            nameLocation: 'end' as const,
            nameGap: 14,
            nameTextStyle: {
                align: 'left' as const,
                padding: [0, 0, 6, 0],
                fontSize: 12,
            },
            position: 'left' as const,
            axisLabel: {
                formatter: yAxisLabelFormatter,
                margin: 10,
            },
        },
    });

    // ECharts option configuration for Temperature/FBE (memoized)
    const temperatureChartOption = useMemo(() => {
        if (!temperatureChartData) {
            console.log('No temperature chart data available');
            return {};
        }

        const series: any[] = [];

        if (isRaptor) {
            // FBE fields: map selectedTemperatureSats (1-20) to FBE field names
            const fbeFields: Array<{ index: number; name: string; label: string }> = [];
            for (let i = 1; i <= 10; i++) {
                fbeFields.push({ index: i, name: `fbeFlow_${i}`, label: `FBE Flow ${i}` });
                fbeFields.push({ index: i + 10, name: `fbeCarrier_${i}`, label: `FBE Carrier ${i}` });
            }

            // Add setpoint series (dashed lines) - only if field is selected
            Object.entries(temperatureChartData.temperatureSetpointData).forEach(([name, values]) => {
                const field = fbeFields.find(f => f.name === name);
                if (field && selectedTemperatureSats.has(field.index)) {
                    series.push({
                        name: `${field.label} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if field is selected
            Object.entries(temperatureChartData.temperatureActualData).forEach(([name, values]) => {
                const field = fbeFields.find(f => f.name === name);
                if (field && selectedTemperatureSats.has(field.index)) {
                    series.push({
                        name: `${field.label} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        } else {
            // Add setpoint series (dashed lines) - only if SAT is selected
            Object.entries(temperatureChartData.temperatureSetpointData).forEach(([name, values]) => {
                const satNumber = parseInt(name.replace('sat_', ''));
                if (selectedTemperatureSats.has(satNumber)) {
                    series.push({
                        name: `${name.replace('sat_', 'SAT TC ')} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if SAT is selected
            Object.entries(temperatureChartData.temperatureActualData).forEach(([name, values]) => {
                const satNumber = parseInt(name.replace('sat_', ''));
                if (selectedTemperatureSats.has(satNumber)) {
                    series.push({
                        name: `${name.replace('sat_', 'SAT TC ')} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        }

        if (series.length === 0) {
            console.warn('No series data for temperature chart');
        }

        return {
            ...buildChartAxisLayout(
                isRaptor ? 'FBE Value' : 'Temperature (°C)',
                isRaptor ? '{value}' : '{value}°C'
            ),
            legend: {
                data: series.map(s => s.name),
                bottom: 0,
                type: 'scroll',
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: temperatureChartData.timeStamps,
                axisLabel: {
                    margin: 12,
                },
            },
            series,
        };
    }, [temperatureChartData, isRaptor, selectedTemperatureSats]);

    // ECharts option configuration for Pressure/CMPP (memoized)
    const pressureChartOption = useMemo(() => {
        if (!pressureChartData) {
            return {};
        }

        const series: any[] = [];

        if (isRaptor) {
            // Add setpoint series (dashed lines) - only if field is selected
            Object.entries(pressureChartData.pressureSetpointData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    const match = name.match(/(\w+)_(\d+)/);
                    let label = name;
                    if (match) {
                        const [, type, num] = match;
                        if (type === 'cmppFlow') {
                            label = `CMPP Flow ${num}`;
                        } else if (type === 'cmppCarrier') {
                            label = `CMPP Carrier ${num}`;
                        }
                    }
                    series.push({
                        name: `${label} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if field is selected
            Object.entries(pressureChartData.pressureActualData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    const match = name.match(/(\w+)_(\d+)/);
                    let label = name;
                    if (match) {
                        const [, type, num] = match;
                        if (type === 'cmppFlow') {
                            label = `CMPP Flow ${num}`;
                        } else if (type === 'cmppCarrier') {
                            label = `CMPP Carrier ${num}`;
                        }
                    }
                    series.push({
                        name: `${label} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        } else {
            // Add setpoint series (dashed lines) - only if field is selected
            Object.entries(pressureChartData.pressureSetpointData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    series.push({
                        name: `${name} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if field is selected
            Object.entries(pressureChartData.pressureActualData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    series.push({
                        name: `${name} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        }

        if (series.length === 0) {
            console.warn('No series data for pressure chart');
        }

        return {
            ...buildChartAxisLayout(
                isRaptor ? 'CMPP Value' : 'Pressure (Bar)',
                isRaptor ? '{value}' : '{value} Bar'
            ),
            legend: {
                data: series.map(s => s.name),
                bottom: 0,
                type: 'scroll',
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: pressureChartData.timeStamps,
                axisLabel: {
                    margin: 12,
                },
            },
            series,
        };
    }, [pressureChartData, isRaptor, selectedPressureFields]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="flex h-[100dvh] max-h-none w-screen max-w-none flex-col overflow-hidden m-0 rounded-none p-0 [&>button]:hidden">
                <DialogHeader className="relative px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0 flex flex-row items-start justify-between">
                    <div className="absolute left-6 top-6">
                        <Button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            variant="outline"
                        >
                            <ArrowLeft className="h-5 w-5 text-gray-500" />
                        </Button>
                    </div>

                    <div className="pl-14">
                        <DialogTitle>Data Graphs</DialogTitle>
                        <DialogDescription>
                            {isRaptor 
                                ? 'View graphical representation of FBE and CMPP data.'
                                : 'View graphical representation of temperature and pressure data.'
                            }
                            {refreshIntervalSeconds && (
                                <span className="ml-2 text-xs text-gray-500">
                                    (Refreshing every {refreshIntervalSeconds} seconds)
                                </span>
                            )}
                        </DialogDescription>
                    </div>
                </DialogHeader>

                {/* Time Range Selector */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-600" />
                            <Label className="text-sm font-medium text-gray-700">Time Range:</Label>
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="15min"
                                    checked={timeRange === '15min'}
                                    onChange={(e) => setTimeRange(e.target.value as GraphTimeRange)}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">15 Minutes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="30min"
                                    checked={timeRange === '30min'}
                                    onChange={(e) => setTimeRange(e.target.value as GraphTimeRange)}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">30 Minutes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="1hour"
                                    checked={timeRange === '1hour'}
                                    onChange={(e) => setTimeRange(e.target.value as GraphTimeRange)}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">1 Hour</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="4hours"
                                    checked={timeRange === '4hours'}
                                    onChange={(e) => setTimeRange(e.target.value as GraphTimeRange)}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">4 Hours</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="daterange"
                                    checked={timeRange === 'daterange'}
                                    onChange={(e) => setTimeRange(e.target.value as GraphTimeRange)}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">Date Range</span>
                            </label>

                            {timeRange === 'daterange' && (
                                <div className="w-full min-w-[260px] sm:w-auto">
                                    <DateRangePicker
                                        showCompare={false}
                                        showTime
                                        align="start"
                                        onUpdate={(values) => {
                                            setDateRange({
                                                dateFrom: values.dateFrom,
                                                dateTo: values.dateTo,
                                            });
                                        }}
                                        initialDateFrom={dateRange.dateFrom}
                                        initialDateTo={dateRange.dateTo}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
                    {isLoading && !graphData && (
                        <div className="text-center py-12 text-gray-500 flex-1 flex items-center justify-center">
                            <div>
                                <Loader2 className="h-16 w-16 mx-auto mb-4 text-gray-400 animate-spin" />
                                <p className="text-lg font-medium">Loading graph data...</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {(!isLoading || temperatureChartData || pressureChartData) && !error && (
                        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'temperature' | 'pressure' | 'fbe' | 'cmpp')} className="flex w-full min-w-0 flex-col">
                            <div className="sticky top-0 z-10 flex-shrink-0 border-b border-gray-200 bg-white px-3 pt-3 sm:px-6 sm:pt-4">
                                <div className="flex items-center gap-1 bg-transparent pb-0">
                                    {canScrollTabsLeft && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="mb-2 shrink-0 text-heritage-grey hover:text-blueprint-blue"
                                            onClick={handleScrollTabsLeft}
                                            aria-label="Scroll tabs left"
                                        >
                                            <ChevronLeft className="h-5 w-5" />
                                        </Button>
                                    )}
                                    <div
                                        ref={tabsContainerRef}
                                        className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                                    >
                                        <TabsList className="h-auto w-max justify-start gap-2 bg-transparent p-0 md:gap-4">
                                            <TabsTrigger
                                                value={isRaptor ? "fbe" : "temperature"}
                                                className="relative shrink-0 whitespace-nowrap rounded-none border-b-[3px] border-transparent px-2 pb-3 pt-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-pink-500 data-[state=active]:text-pink-500 sm:px-4 sm:text-base"
                                            >
                                                <div className="flex items-center gap-1.5 sm:gap-2">
                                                    <Thermometer className="h-4 w-4 sm:h-5 sm:w-5" />
                                                    <span>{isRaptor ? 'FBE' : 'Temperature Control Dashboard'}</span>
                                                </div>
                                            </TabsTrigger>
                                            <TabsTrigger
                                                value={isRaptor ? "cmpp" : "pressure"}
                                                className="relative shrink-0 whitespace-nowrap rounded-none border-b-[3px] border-transparent px-2 pb-3 pt-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-purple-400 data-[state=active]:text-purple-400 sm:px-4 sm:text-base"
                                            >
                                                <div className="flex items-center gap-1.5 sm:gap-2">
                                                    <Cloud className="h-4 w-4 sm:h-5 sm:w-5" />
                                                    <span>{isRaptor ? 'CMPP' : 'Pressure Control Dashboard'}</span>
                                                </div>
                                            </TabsTrigger>
                                        </TabsList>
                                    </div>
                                    {canScrollTabsRight && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="mb-2 shrink-0 text-heritage-grey hover:text-blueprint-blue"
                                            onClick={handleScrollTabsRight}
                                            aria-label="Scroll tabs right"
                                        >
                                            <ChevronRight className="h-5 w-5" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="min-w-0">
                                <TabsContent value={isRaptor ? "fbe" : "temperature"} className="m-0 mt-0 p-0 data-[state=inactive]:hidden">
                                    {temperatureChartData ? (
                                        <>
                                            {/* Temperature/FBE Point Selection Grid - Always on top, outside scrollable area */}
                                            <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 pb-3 pt-3 sm:px-6 sm:pb-4 sm:pt-4">
                                                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        <Filter className="h-4 w-4 shrink-0 text-gray-600" />
                                                        <Label className="text-xs font-medium text-gray-700 sm:text-sm">{isRaptor ? 'Select FBE Fields:' : 'Select Temperature Points:'}</Label>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setShowTemperatureFilter(!showTemperatureFilter)}
                                                        className="h-7 text-xs"
                                                    >
                                                        {showTemperatureFilter ? 'Hide' : 'Show'} Filter
                                                    </Button>
                                                </div>
                                                {showTemperatureFilter && (
                                                    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 sm:max-h-64 sm:p-4">
                                                        {isRaptor ? (
                                                            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-5">
                                                                {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => {
                                                                    const flowIndex = num;
                                                                    const carrierIndex = num + 10;
                                                                    const isFlowSelected = selectedTemperatureSats.has(flowIndex);
                                                                    const isCarrierSelected = selectedTemperatureSats.has(carrierIndex);

                                                                    return (
                                                                        <React.Fragment key={`fbe_${num}`}>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isFlowSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedTemperatureSats);
                                                                                    if (isFlowSelected) {
                                                                                        newSet.delete(flowIndex);
                                                                                    } else {
                                                                                        newSet.add(flowIndex);
                                                                                    }
                                                                                    setSelectedTemperatureSats(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isFlowSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    FBE Flow {num}
                                                                                </span>
                                                                            </div>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isCarrierSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedTemperatureSats);
                                                                                    if (isCarrierSelected) {
                                                                                        newSet.delete(carrierIndex);
                                                                                    } else {
                                                                                        newSet.add(carrierIndex);
                                                                                    }
                                                                                    setSelectedTemperatureSats(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isCarrierSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    FBE Carrier {num}
                                                                                </span>
                                                                            </div>
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10">
                                                                {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => {
                                                                    const isSelected = selectedTemperatureSats.has(num);

                                                                    return (
                                                                        <div 
                                                                            key={`sat_${num}`} 
                                                                            className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                isSelected 
                                                                                    ? 'bg-green-100 border-green-400' 
                                                                                    : 'bg-white hover:bg-gray-50'
                                                                            }`}
                                                                            onClick={() => {
                                                                                const newSet = new Set(selectedTemperatureSats);
                                                                                if (isSelected) {
                                                                                    newSet.delete(num);
                                                                                } else {
                                                                                    newSet.add(num);
                                                                                }
                                                                                setSelectedTemperatureSats(newSet);
                                                                            }}
                                                                        >
                                                                            <span className={`text-xs font-semibold ${
                                                                                isSelected 
                                                                                    ? 'text-green-700' 
                                                                                    : 'text-gray-700'
                                                                            }`}>
                                                                                SAT TC {num}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className="mt-3 flex gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const allFields = new Set<number>();
                                                                    if (isRaptor) {
                                                                        for (let i = 1; i <= 20; i++) {
                                                                            allFields.add(i);
                                                                        }
                                                                    } else {
                                                                        for (let i = 1; i <= 30; i++) {
                                                                            allFields.add(i);
                                                                        }
                                                                    }
                                                                    setSelectedTemperatureSats(allFields);
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                Select All
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setSelectedTemperatureSats(new Set())}
                                                                className="h-7 text-xs"
                                                            >
                                                                Deselect All
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Graph - Below the filter */}
                                            <div className="p-3 pb-6 sm:p-6">
                                                <div className="relative h-[min(55vh,420px)] min-h-[300px] w-full overflow-visible rounded-lg border border-gray-200 p-3 pt-5 sm:h-[min(60vh,480px)] sm:min-h-[320px] sm:p-4 sm:pt-6">
                                                    <ReactECharts
                                                        option={temperatureChartOption}
                                                        style={{ height: '100%', width: '100%', minHeight: 300 }}
                                                        opts={{ renderer: 'canvas' }}
                                                        notMerge
                                                        lazyUpdate
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="w-full h-full border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                                            <div className="text-center text-gray-500">
                                                <LineChart className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                                                <p className="text-sm">{isRaptor ? 'No FBE data available' : 'No temperature data available'}</p>
                                            </div>
                                        </div>
                                    )}
                                </TabsContent>

                                <TabsContent value={isRaptor ? "cmpp" : "pressure"} className="m-0 mt-0 p-0 data-[state=inactive]:hidden">
                                    {pressureChartData ? (
                                        <>
                                            {/* Pressure/CMPP Field Selection Grid */}
                                            <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 pb-3 pt-3 sm:px-6 sm:pb-4 sm:pt-4">
                                                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                    <div className="flex min-w-0 items-center gap-2">
                                                        <Filter className="h-4 w-4 shrink-0 text-gray-600" />
                                                        <Label className="text-xs font-medium text-gray-700 sm:text-sm">{isRaptor ? 'Select CMPP Fields:' : 'Select Pressure Fields:'}</Label>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setShowPressureFilter(!showPressureFilter)}
                                                        className="h-7 text-xs"
                                                    >
                                                        {showPressureFilter ? 'Hide' : 'Show'} Filter
                                                    </Button>
                                                </div>
                                                {showPressureFilter && (
                                                    <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 sm:max-h-64 sm:p-4">
                                                        {isRaptor ? (
                                                            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 md:grid-cols-5">
                                                                {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => {
                                                                    const flowField = `cmppFlow_${num}`;
                                                                    const carrierField = `cmppCarrier_${num}`;
                                                                    const isFlowSelected = selectedPressureFields.has(flowField);
                                                                    const isCarrierSelected = selectedPressureFields.has(carrierField);

                                                                    return (
                                                                        <React.Fragment key={`cmpp_${num}`}>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isFlowSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedPressureFields);
                                                                                    if (isFlowSelected) {
                                                                                        newSet.delete(flowField);
                                                                                    } else {
                                                                                        newSet.add(flowField);
                                                                                    }
                                                                                    setSelectedPressureFields(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isFlowSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    CMPP Flow {num}
                                                                                </span>
                                                                            </div>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isCarrierSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedPressureFields);
                                                                                    if (isCarrierSelected) {
                                                                                        newSet.delete(carrierField);
                                                                                    } else {
                                                                                        newSet.add(carrierField);
                                                                                    }
                                                                                    setSelectedPressureFields(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isCarrierSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    CMPP Carrier {num}
                                                                                </span>
                                                                            </div>
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
                                                                {['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure'].map((field) => {
                                                                    const isSelected = selectedPressureFields.has(field);
                                                                    const displayName = field
                                                                        .replace(/([A-Z])/g, ' $1')
                                                                        .replace(/^./, str => str.toUpperCase())
                                                                        .trim();

                                                                    return (
                                                                        <div 
                                                                            key={field} 
                                                                            className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                isSelected 
                                                                                    ? 'bg-green-100 border-green-400' 
                                                                                    : 'bg-white hover:bg-gray-50'
                                                                            }`}
                                                                            onClick={() => {
                                                                                const newSet = new Set(selectedPressureFields);
                                                                                if (isSelected) {
                                                                                    newSet.delete(field);
                                                                                } else {
                                                                                    newSet.add(field);
                                                                                }
                                                                                setSelectedPressureFields(newSet);
                                                                            }}
                                                                        >
                                                                            <span className={`text-xs font-semibold ${
                                                                                isSelected 
                                                                                    ? 'text-green-700' 
                                                                                    : 'text-gray-700'
                                                                            }`}>
                                                                                {displayName}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className="mt-3 flex gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (isRaptor) {
                                                                        const allFields = new Set<string>();
                                                                        for (let i = 1; i <= 10; i++) {
                                                                            allFields.add(`cmppFlow_${i}`);
                                                                            allFields.add(`cmppCarrier_${i}`);
                                                                        }
                                                                        setSelectedPressureFields(allFields);
                                                                    } else {
                                                                        setSelectedPressureFields(new Set(['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure']));
                                                                    }
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                Select All
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setSelectedPressureFields(new Set())}
                                                                className="h-7 text-xs"
                                                            >
                                                                Deselect All
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Graph - Below the filter */}
                                            <div className="p-3 pb-6 sm:p-6">
                                                <div className="relative h-[min(55vh,420px)] min-h-[300px] w-full overflow-visible rounded-lg border border-gray-200 p-3 pt-5 sm:h-[min(60vh,480px)] sm:min-h-[320px] sm:p-4 sm:pt-6">
                                                    <ReactECharts
                                                        option={pressureChartOption}
                                                        style={{ height: '100%', width: '100%', minHeight: 300 }}
                                                        opts={{ renderer: 'canvas' }}
                                                        notMerge
                                                        lazyUpdate
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="w-full h-full border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                                            <div className="text-center text-gray-500">
                                                <LineChart className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                                                <p className="text-sm">{isRaptor ? 'No CMPP data available' : 'No pressure data available'}</p>
                                            </div>
                                        </div>
                                    )}
                                </TabsContent>
                            </div>
                        </Tabs>
                    )}

                    {isLoading && !temperatureChartData && !pressureChartData && (
                        <div className="text-center py-12 text-gray-500 flex-1 flex items-center justify-center">
                            <div>
                                <Loader2 className="h-16 w-16 mx-auto mb-4 text-gray-400 animate-spin" />
                                <p className="text-lg font-medium">Loading graph data...</p>
                            </div>
                        </div>
                    )}

                    {!isLoading && !error && !temperatureChartData && !pressureChartData && (
                        <div className="text-center py-12 text-gray-500 flex-1 flex items-center justify-center">
                            <div>
                                <LineChart className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <p className="text-lg font-medium">No data available</p>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="px-6 py-4 border-t border-gray-200 flex-shrink-0" />
            </DialogContent>
        </Dialog>
    );
};
